import type { OutcomeItem } from "@/services/financeiro";
import { filterOutcomeByCostCenter } from "@/pages/Gerencial/chartUtils";
import type { ChaoPhysicalMonthPoint } from "@/services/chaoDeObra";

export interface ChaoAvancoChartPoint {
  mes: string;
  mesKey: string;
  /** Rótulo completo do período (tooltip). */
  periodo: string;
  fisico: number;
  financeiro: number;
}

export type ChaoAvancoGranularity = "month" | "week";

const MES_LABELS = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatLocalYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseLocalYmd(ymd: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const y = parseInt(ymd.slice(0, 4), 10);
  const m = parseInt(ymd.slice(5, 7), 10);
  const day = parseInt(ymd.slice(8, 10), 10);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day);
}

function mondayOfLocalDate(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
}

function formatDayMonth(d: Date): string {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

export function formatChaoMonthLabel(mesKey: string): string {
  if (!mesKey || mesKey.length < 7) return "—";
  const [yearStr, monthStr] = mesKey.split("-");
  const year = (yearStr ?? "????").slice(2);
  const month = parseInt(monthStr ?? "0", 10) - 1;
  return `${MES_LABELS[month] ?? "??"}/${year}`;
}

export function formatChaoWeekLabel(weekKey: string): string {
  const d = parseLocalYmd(weekKey);
  if (!d) return "—";
  return formatDayMonth(d);
}

export function formatChaoWeekRangeLabel(weekKey: string): string {
  const d = parseLocalYmd(weekKey);
  if (!d) return "—";
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
  return `${formatDayMonth(d)}–${formatDayMonth(end)}`;
}

/** Últimos N meses-calendário (yyyy-mm). */
export function lastNMonthKeys(monthCount: number): string[] {
  const n = Math.max(1, Math.min(36, monthCount));
  const keys: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  }
  return keys;
}

/** Últimas N semanas (segunda-feira yyyy-mm-dd). */
export function lastNWeekKeys(weekCount: number, now = new Date()): string[] {
  const n = Math.max(1, Math.min(52, weekCount));
  const keys: string[] = [];
  const nowMonday = mondayOfLocalDate(now);
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(nowMonday.getFullYear(), nowMonday.getMonth(), nowMonday.getDate() - i * 7);
    keys.push(formatLocalYmd(d));
  }
  return keys;
}

export function weekKeyFromDateStr(dateStr: string): string | null {
  const ymd = dateStr.trim().substring(0, 10);
  const d = parseLocalYmd(ymd);
  if (!d) return null;
  return formatLocalYmd(mondayOfLocalDate(d));
}

function outcomeEffectiveDate(item: OutcomeItem): string {
  return (item.paymentDate || item.dueDate || item.issueDate || "").trim();
}

/** Soma parcelas Gerencial (planos 210–202) dos centros de custo informados. */
export function sumGerencialForCostCenters(outcome: OutcomeItem[], ccIds: string[]): number {
  let total = 0;
  for (const cc of ccIds) {
    for (const item of filterOutcomeByCostCenter(outcome, cc)) {
      total += item.originalAmount;
    }
  }
  return total;
}

/** Valor total das unidades de imóvel = Σ (valor médio × quantidade de unidades por CC). */
export function computeValorTotalImoveis(
  unitCountByCc: Record<string, number>,
  avgByCc: Record<string, number>,
  ccIds: string[],
): number {
  let total = 0;
  for (const cc of ccIds) {
    const units = unitCountByCc[cc] ?? 0;
    const avg = avgByCc[cc] ?? 0;
    if (units > 0 && avg > 0) total += units * avg;
  }
  return total;
}

export function pctAvancoFinanceiro(gerencialTotal: number, valorTotalImoveis: number): number {
  if (valorTotalImoveis <= 0) return 0;
  return (gerencialTotal / valorTotalImoveis) * 100;
}

/** Valor realizado após dedução manual (somente Chão de Obra). */
export function applyFinancialDeduction(gerencialTotal: number, deduction: number): number {
  const d = Number.isFinite(deduction) && deduction > 0 ? deduction : 0;
  return Math.max(0, gerencialTotal - d);
}

/** Soma deduções dos centros de custo informados. */
export function sumFinancialDeductionForCostCenters(
  deductionByCc: Record<string, number>,
  ccIds: string[],
): number {
  let total = 0;
  for (const cc of ccIds) {
    const v = deductionByCc[cc] ?? 0;
    if (Number.isFinite(v) && v > 0) total += v;
  }
  return total;
}

/** Variação em pontos percentuais vs mês anterior na série. */
export function monthDeltaPp(
  series: ChaoAvancoChartPoint[],
  key: "fisico" | "financeiro",
): number | null {
  if (series.length < 2) return null;
  const last = series[series.length - 1]![key];
  const prev = series[series.length - 2]![key];
  return Math.round((last - prev) * 10) / 10;
}

function periodKeyFromOutcomeDate(dateStr: string, granularity: ChaoAvancoGranularity): string | null {
  if (granularity === "week") return weekKeyFromDateStr(dateStr);
  const mk = dateStr.substring(0, 7);
  return mk.length === 7 ? mk : null;
}

/**
 * Série alinhada aos últimos N meses ou semanas:
 * financeiro = valor realizado acumulado ÷ valor orçado (dedução aplicada ao total);
 * físico por período (API). O acumulado financeiro inclui parcelas anteriores à janela exibida.
 */
export function buildChaoAvancoChartSeries(
  outcome: OutcomeItem[],
  ccIds: string[],
  physicalSeries: ChaoPhysicalMonthPoint[],
  valorTotalImoveis: number,
  periodCount = 7,
  financialDeduction = 0,
  granularity: ChaoAvancoGranularity = "month",
): ChaoAvancoChartPoint[] {
  const periodKeys =
    granularity === "week" ? lastNWeekKeys(periodCount) : lastNMonthKeys(periodCount);
  const physicalMap = new Map(physicalSeries.map((p) => [p.mesKey, p.pct]));

  const byPeriod = new Map<string, number>();
  let undatedTotal = 0;
  for (const cc of ccIds) {
    for (const item of filterOutcomeByCostCenter(outcome, cc)) {
      const d = outcomeEffectiveDate(item);
      const pk = periodKeyFromOutcomeDate(d, granularity);
      if (!pk) {
        undatedTotal += item.originalAmount;
        continue;
      }
      byPeriod.set(pk, (byPeriod.get(pk) ?? 0) + item.originalAmount);
    }
  }

  const firstKey = periodKeys[0] ?? "";
  let cumulative = undatedTotal;
  for (const [pk, amount] of byPeriod) {
    if (firstKey && pk < firstKey) cumulative += amount;
  }

  const series: ChaoAvancoChartPoint[] = [];
  const lastPhysicalKey = physicalSeries[physicalSeries.length - 1]?.mesKey;

  for (const pk of periodKeys) {
    cumulative += byPeriod.get(pk) ?? 0;
    const adjustedCumulative = applyFinancialDeduction(cumulative, financialDeduction);
    const rawFisico =
      physicalMap.get(pk) ??
      (lastPhysicalKey && pk >= lastPhysicalKey ? physicalMap.get(lastPhysicalKey) : 0) ??
      0;
    const mes = granularity === "week" ? formatChaoWeekLabel(pk) : formatChaoMonthLabel(pk);
    series.push({
      mesKey: pk,
      mes,
      periodo: granularity === "week" ? formatChaoWeekRangeLabel(pk) : mes,
      fisico: Math.min(100, Math.max(0, rawFisico)),
      financeiro:
        valorTotalImoveis > 0 ? pctAvancoFinanceiro(adjustedCumulative, valorTotalImoveis) : 0,
    });
  }

  return series;
}
